import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const SiteContext = createContext({
  site: null,
  pageMeta: {},
  loading: true,
  refresh: () => {},
  loadPageMeta: () => {},
});

export function SiteProvider({ children }) {
  const [site, setSite] = useState(null);
  const [pageMeta, setPageMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const pageMetaRef = useRef({});
  const pageMetaRequestsRef = useRef(new Map());

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/site");
      setSite(data);
    } catch {
      setSite(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPageMeta = useCallback((slug) => {
    if (pageMetaRef.current[slug]) {
      return Promise.resolve(pageMetaRef.current[slug]);
    }

    const pendingRequest = pageMetaRequestsRef.current.get(slug);
    if (pendingRequest) return pendingRequest;

    const request = api
      .get(`/page-meta/${slug}`)
      .then(({ data }) => {
        const nextPageMeta = { ...pageMetaRef.current, [slug]: data };
        pageMetaRef.current = nextPageMeta;
        setPageMeta(nextPageMeta);
        return data;
      })
      .catch(() => null)
      .finally(() => {
        pageMetaRequestsRef.current.delete(slug);
      });

    pageMetaRequestsRef.current.set(slug, request);
    return request;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SiteContext.Provider value={{ site, pageMeta, loading, refresh, loadPageMeta }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  return useContext(SiteContext);
}
